import type { HlsTelemetrySnapshot } from "api-contracts/rpc/hls-telemetry";

export interface HlsTelemetrySnapshotV2 {
  session_id: string;
  stream_id: string;
  timestamp: number;
  current_level: string;
  current_bitrate: number;
  bandwidth_estimate: number;
  dropped_frames_delta: number;
  buffer_stalls_delta: number;
  frag_errors_delta: number;
  session_duration_seconds: number;
  avg_bitrate: number;
  total_dropped_frames: number;
  total_buffer_stalls: number;
  total_frag_errors: number;
  total_quality_switches: number;
  quality_score: "Critical" | "Moderate" | "Good" | "Excellent";
  browser?: string;
  browser_version?: string;
  operating_system?: string;
}

class HlsTelemetryService {
  private static instance: HlsTelemetryService;
  private snapshots: HlsTelemetrySnapshot[] = [];
  private flushInterval: number | null = null;
  private readonly FLUSH_INTERVAL_MS = 10000;
  private readonly MAX_SNAPSHOTS = 50;

  static getInstance(): HlsTelemetryService {
    if (!HlsTelemetryService.instance) {
      HlsTelemetryService.instance = new HlsTelemetryService();
    }
    return HlsTelemetryService.instance;
  }

  private constructor() {}

  start(): void {
    if (this.flushInterval !== null) return;
    this.flushInterval = window.setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);
  }

  stop(): void {
    if (this.flushInterval !== null) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush();
  }

  record(snapshot: HlsTelemetrySnapshot): void {
    this.snapshots.push(snapshot);
    if (this.snapshots.length >= this.MAX_SNAPSHOTS) {
      this.flush();
    }
  }

  private async flush(): Promise<void> {
    if (this.snapshots.length === 0) return;
    const toSend = [...this.snapshots];
    this.snapshots = [];
    try {
      await fetch("/api/hls-telemetry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snapshots: toSend }),
        credentials: "include",
      });
    } catch (error) {
      console.warn("Failed to send HLS telemetry:", error);
    }
  }

  /**
   * Record v2 snapshot - sends immediately (1 row per request every 10s).
   * No batching since each row contains pre-computed aggregates.
   */
  recordV2(snapshot: HlsTelemetrySnapshotV2): void {
    fetch("/api/hls-telemetry-v2", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ snapshot }),
      credentials: "include",
    }).catch((error) => {
      console.warn("Failed to send HLS telemetry v2:", error);
    });
  }
}

export const hlsTelemetryService = HlsTelemetryService.getInstance();
